// src/tabs/ProfileTab.jsx
import React, { useContext, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Avatar,
  Button,
  Divider,
  Box,
  Chip,
  IconButton,
} from "@mui/material";

import PersonIcon from "@mui/icons-material/Person";
import StoreIcon from "@mui/icons-material/Store";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AddIcon from "@mui/icons-material/Add";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import GavelIcon from "@mui/icons-material/Gavel";
import PrivacyTipIcon from "@mui/icons-material/PrivacyTip";
import ArticleIcon from "@mui/icons-material/Article";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import LogoutIcon from "@mui/icons-material/Logout";
import VerifiedIcon from "@mui/icons-material/Verified";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import EventIcon from "@mui/icons-material/Event";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { formatDate } from "../../../utils/helpers";
import { useOutletContext } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";

/* ─────────────────────────────────────────────
   Keyframes
───────────────────────────────────────────── */
const ANIMATION_STYLES = `
  @keyframes floatBlob {
    0%   { transform: translate(0px,  0px)  scale(1);    }
    33%  { transform: translate(30px,-30px) scale(1.05); }
    66%  { transform: translate(-20px,20px) scale(0.97); }
    100% { transform: translate(0px,  0px)  scale(1);    }
  }
  @keyframes floatBlob2 {
    0%   { transform: translate(0px,  0px)  scale(1);    }
    33%  { transform: translate(-25px,25px) scale(1.04); }
    66%  { transform: translate(20px,-15px) scale(0.98); }
    100% { transform: translate(0px,  0px)  scale(1);    }
  }
  @keyframes fadeSlideUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0);    }
  }
  @keyframes avatarPop {
    0%   { transform: scale(0.85); opacity: 0; }
    60%  { transform: scale(1.05); }
    100% { transform: scale(1);    opacity: 1; }
  }
`;

/* ─────────────────────────────────────────────
   Background blob
───────────────────────────────────────────── */
function GradientBlob({ sx }) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: "fixed",
        borderRadius: "50%",
        filter: "blur(72px)",
        pointerEvents: "none",
        zIndex: 0,
        ...sx,
      }}
    />
  );
}

/* ─────────────────────────────────────────────
   Section header — reused across all sections
───────────────────────────────────────────── */
function SectionHeader({ icon, title, action }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 0.2,
            bgcolor: "#f0fdf4",
            border: "1px solid #dcfce7",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 15, color: "#16a34a" } })}
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: { xs: 13, sm: 14 }, color: "#111827" }}>
          {title}
        </Typography>
      </Stack>
      {action}
    </Stack>
  );
}

/* ─────────────────────────────────────────────
   Legal / Support link row
───────────────────────────────────────────── */
function LinkRow({ icon, label, href, isExternal = false, isLast = false }) {
  return (
    <>
      <Box
        component="a"
        href={href}
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noopener noreferrer" : undefined}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.2,
          px: 0,
          textDecoration: "none",
          color: "inherit",
          borderRadius: 0.2,
          mx: -0.5,
          px: 0.5,
          cursor: "pointer",
          transition: "background 0.15s ease",
          // Touch-friendly min height
          minHeight: 44,
          "&:hover": { bgcolor: "#f8fafc" },
          "&:active": { bgcolor: "#f1f5f9" },
        }}
      >
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 0.2,
              bgcolor: "#f8fafc",
              border: "1px solid #e8f0fe",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: 15, color: "#64748b" } })}
          </Box>
          <Typography sx={{ fontSize: { xs: 12, sm: 13 }, color: "#374151", fontWeight: 500 }}>
            {label}
          </Typography>
        </Stack>
        {isExternal ? (
          <OpenInNewIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
        ) : (
          <ChevronRightIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
        )}
      </Box>
      {!isLast && <Divider sx={{ borderColor: "#f8fafc" }} />}
    </>
  );
}

/* ─────────────────────────────────────────────
   Main component
───────────────────────────────────────────── */
export default function ProfileTab() {
  const {
    devices,
    addresses,
    retailerInfo,
    onAddAddress,
    onEditAddress,
    onViewPurchase,
    onDeleteAddress,
    deviceinprofile,
  } = useOutletContext();

  const { user, logout } = useContext(AuthContext);

  /* ── Business logic — UNCHANGED ── */
  const purchases = [
    {
      id: "pur-1",
      model_name: "Google Pixel 8",
      purchase_date: "2025-12-20",
      order_no: "ORD-77881",
      serial_no: "SN-PXL8-88991234",
      imei: "356789101112131",
      warranty_expiry: "2026-12-20",
      purchased_from: { name: "Akash Mobile World" },
    },
    {
      id: "pur-2",
      model_name: "Samsung Galaxy S24",
      purchase_date: "2025-11-08",
      order_no: "ORD-66420",
      serial_no: "SN-S24-10012025",
      imei: "357891011121314",
      warranty_expiry: "2026-11-08",
      purchased_from: { name: "City Mobiles" },
    },
  ];

  const purchasesSorted = useMemo(() => {
    return [...purchases].sort(
      (a, b) => new Date(b.purchase_date) - new Date(a.purchase_date)
    );
  }, []);

  const uniqueShopCount = new Set(
    purchases.map((p) => p.purchased_from?.name?.toLowerCase())
  ).size;

  /* ── Derived display values ── */
  const userInitial = (user?.name || "U").charAt(0).toUpperCase();
  const userName = user?.name || "User";
  const userContact = user?.email || user?.phone || "—";

  return (
    <>
      <style>{ANIMATION_STYLES}</style>

      {/* ── Background blobs ── */}
      <GradientBlob
        sx={{
          width: { xs: 200, sm: 300 },
          height: { xs: 200, sm: 300 },
          top: -60,
          right: -80,
          background: "radial-gradient(circle, rgba(34,197,94,0.16) 0%, rgba(16,185,129,0.07) 70%)",
          animation: "floatBlob 20s ease-in-out infinite",
        }}
      />
      <GradientBlob
        sx={{
          width: { xs: 160, sm: 240 },
          height: { xs: 160, sm: 240 },
          bottom: 120,
          left: -70,
          background: "radial-gradient(circle, rgba(59,130,246,0.14) 0%, rgba(99,102,241,0.06) 70%)",
          animation: "floatBlob2 24s ease-in-out infinite",
        }}
      />

      {/* ── Content ── */}
      <Stack
        spacing={2}
        sx={{
          position: "relative",
          zIndex: 1,
          animation: "fadeSlideUp 0.4s ease both",
          overflowX: "hidden",
          // Extra bottom padding so logout button isn't hidden by bottom nav
          pb: 2,
        }}
      >

        {/* ══════════════════════════════
            PROFILE HERO CARD
        ══════════════════════════════ */}
        <Card
          sx={{
            borderRadius: 0.2,
            background: "linear-gradient(135deg, #16a34a 0%, #0d9488 60%, #0369a1 100%)",
            boxShadow: "0 8px 32px rgba(22,163,74,0.28)",
            border: "none",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* Shine overlay */}
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(135deg, rgba(255,255,255,0.12) 0%, transparent 55%)",
              pointerEvents: "none",
            }}
          />

          <CardContent
            sx={{
              position: "relative",
              px: { xs: 2, sm: 3 },
              py: { xs: 2.5, sm: 3 },
              pb: { xs: "20px !important", sm: "24px !important" },
            }}
          >
            <Stack alignItems="center" spacing={1.5}>

              {/* Avatar circle */}
              <Box
                sx={{
                  width: { xs: 72, sm: 84 },
                  height: { xs: 72, sm: 84 },
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.2)",
                  border: "2.5px solid rgba(255,255,255,0.45)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: { xs: 28, sm: 34 },
                  fontWeight: 800,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
                  backdropFilter: "blur(8px)",
                  animation: "avatarPop 0.5s ease both 0.1s",
                  flexShrink: 0,
                }}
              >
                {userInitial}
              </Box>

              {/* Name */}
              <Box textAlign="center">
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: 18, sm: 20 },
                    color: "#fff",
                    letterSpacing: "-0.3px",
                    lineHeight: 1.2,
                  }}
                >
                  {userName}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: 12, sm: 13 },
                    color: "rgba(255,255,255,0.78)",
                    mt: 0.4,
                    // Long emails should wrap
                    wordBreak: "break-all",
                    textAlign: "center",
                  }}
                >
                  {userContact}
                </Typography>
              </Box>

              {/* Edit profile button */}
              <Button
                size="small"
                startIcon={<PersonIcon sx={{ fontSize: "14px !important" }} />}
                sx={{
                  mt: 0.5,
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: { xs: 11, sm: 12 },
                  textTransform: "none",
                  borderRadius: 0.2,
                  border: "1px solid rgba(255,255,255,0.35)",
                  bgcolor: "rgba(255,255,255,0.12)",
                  backdropFilter: "blur(6px)",
                  px: 2,
                  minHeight: 34,
                  "&:hover": { bgcolor: "rgba(255,255,255,0.20)" },
                }}
              >
                Edit Profile
              </Button>

            </Stack>
          </CardContent>
        </Card>

        {/* ══════════════════════════════
            WARRANTY PURCHASES
        ══════════════════════════════ */}
        <Card
          sx={{
            borderRadius: 0.2,
            border: "1px solid #e8f0fe",
            boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
            bgcolor: "#fff",
          }}
        >
          <CardContent sx={{ px: { xs: 1.5, sm: 2 }, pt: { xs: 1.5, sm: 2 }, pb: "16px !important" }}>

            <SectionHeader
              icon={<StoreIcon />}
              title="Warranty Purchases"
              action={
                <Button
                  size="small"
                  startIcon={<ListAltIcon sx={{ fontSize: "13px !important" }} />}
                  onClick={onViewPurchase}
                  sx={{
                    textTransform: "none",
                    fontSize: { xs: 10, sm: 11 },
                    fontWeight: 600,
                    borderRadius: 0.2,
                    color: "#2563eb",
                    minHeight: 30,
                    "&:hover": { bgcolor: "#eff6ff" },
                  }}
                >
                  View All
                </Button>
              }
            />

            <Typography
              sx={{
                fontSize: { xs: 10, sm: 11 },
                color: "#94a3b8",
                mt: 0.6,
                mb: 1.4,
              }}
            >
              {purchases.length} purchase{purchases.length !== 1 ? "s" : ""}&nbsp;·&nbsp;
              {uniqueShopCount} shop{uniqueShopCount !== 1 ? "s" : ""}
            </Typography>

            <Divider sx={{ borderColor: "#f1f5f9", mb: 1.4 }} />

            <Stack spacing={1.2}>
              {devices.slice(0, 3).map((p) => {
                const isActive = p.expiry_date && new Date(p.expiry_date) > new Date();

                return (
                  <Card
                    key={p.id}
                    elevation={0}
                    sx={{
                      borderRadius: 0.2,
                      border: "1px solid #e8f0fe",
                      bgcolor: "#fafafa",
                    }}
                  >
                    <CardContent sx={{ p: { xs: "12px !important", sm: "14px !important" } }}>

                      {/* Device name row */}
                      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: 12, sm: 13 },
                            color: "#111827",
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {p.brand_name} {p.product_name}
                        </Typography>

                        {/* Active/Expired badge */}
                        <Chip
                          icon={
                            isActive ? (
                              <VerifiedIcon sx={{ fontSize: "11px !important", color: "#16a34a !important" }} />
                            ) : undefined
                          }
                          label={isActive ? "Active" : "Expired"}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: 9,
                            fontWeight: 700,
                            flexShrink: 0,
                            bgcolor: isActive ? "#f0fdf4" : "#fef2f2",
                            color: isActive ? "#15803d" : "#dc2626",
                            border: `1px solid ${isActive ? "#bbf7d0" : "#fecaca"}`,
                            borderRadius: 0.2,
                            "& .MuiChip-icon": { ml: "4px" },
                          }}
                        />
                      </Stack>

                      {/* Retailer + date */}
                      <Typography
                        sx={{
                          fontSize: { xs: 10, sm: 11 },
                          color: "#64748b",
                          mt: 0.4,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {retailerInfo?.business_name || "—"}&nbsp;·&nbsp;
                        {p.created_at ? formatDate(p.created_at) : "—"}
                      </Typography>

                      {/* Meta row: order + expiry */}
                      <Stack
                        direction="row"
                        spacing={{ xs: 1.2, sm: 2 }}
                        mt={1}
                        flexWrap="wrap"
                        sx={{ gap: 0.8 }}
                      >
                        {p.w_code && (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <ArticleIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                            <Typography
                              sx={{
                                fontSize: { xs: 9, sm: 10 },
                                color: "#64748b",
                                fontFamily: "monospace",
                                fontWeight: 600,
                              }}
                            >
                              {p.w_code}
                            </Typography>
                          </Stack>
                        )}

                        {p.expiry_date && (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <EventIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                            <Typography
                              sx={{ fontSize: { xs: 9, sm: 10 }, color: "#64748b", fontWeight: 600 }}
                            >
                              {formatDate(p.expiry_date)}
                            </Typography>
                          </Stack>
                        )}

                        {p.imei1 && (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <FingerprintIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                            <Typography
                              sx={{
                                fontSize: { xs: 9, sm: 10 },
                                color: "#64748b",
                                fontFamily: "monospace",
                                fontWeight: 600,
                                maxWidth: { xs: 100, sm: 140 },
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {p.imei1}
                            </Typography>
                          </Stack>
                        )}
                      </Stack>

                    </CardContent>
                  </Card>
                );
              })}
            </Stack>

          </CardContent>
        </Card>

        {/* ══════════════════════════════
            MY ADDRESSES
        ══════════════════════════════ */}
        <Card
          sx={{
            borderRadius: 0.2,
            border: "1px solid #e8f0fe",
            boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
            bgcolor: "#fff",
          }}
        >
          <CardContent sx={{ px: { xs: 1.5, sm: 2 }, pt: { xs: 1.5, sm: 2 }, pb: "16px !important" }}>

            <SectionHeader
              icon={<LocationOnIcon />}
              title="My Addresses"
              action={
                <Button
                  size="small"
                  startIcon={<AddIcon sx={{ fontSize: "13px !important" }} />}
                  onClick={onAddAddress}
                  sx={{
                    textTransform: "none",
                    fontSize: { xs: 10, sm: 11 },
                    fontWeight: 700,
                    borderRadius: 0.2,
                    color: "#16a34a",
                    border: "1px solid #bbf7d0",
                    bgcolor: "#f0fdf4",
                    minHeight: 30,
                    "&:hover": { bgcolor: "#dcfce7" },
                  }}
                >
                  Add
                </Button>
              }
            />

            <Divider sx={{ borderColor: "#f1f5f9", mt: 1.4, mb: 1.4 }} />

            {addresses.length === 0 ? (
              <Stack alignItems="center" py={3} spacing={1}>
                <LocationOnIcon sx={{ fontSize: 28, color: "#e2e8f0" }} />
                <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                  No addresses saved yet
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={1.2}>
                {addresses.map((a) => (
                  <Card
                    key={a.id}
                    elevation={0}
                    sx={{
                      borderRadius: 0.2,
                      border: "1px solid #e8f0fe",
                      bgcolor: "#fafafa",
                    }}
                  >
                    <CardContent sx={{ p: { xs: "12px !important", sm: "14px !important" } }}>
                      <Stack direction="row" spacing={1.2} alignItems="flex-start">

                        {/* Pin icon tile */}
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 0.2,
                            bgcolor: "#fff7ed",
                            border: "1px solid #fed7aa",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            mt: 0.2,
                          }}
                        >
                          <LocationOnIcon sx={{ fontSize: 16, color: "#ea580c" }} />
                        </Box>

                        {/* Address text */}
                        <Box flex={1} minWidth={0}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: { xs: 12, sm: 13 },
                              color: "#111827",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {a.name}
                          </Typography>

                          <Typography
                            sx={{ fontSize: { xs: 10, sm: 11 }, color: "#64748b", mt: 0.2 }}
                          >
                            {a.mobile}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: { xs: 10, sm: 11 },
                              color: "#64748b",
                              mt: 0.3,
                              lineHeight: 1.5,
                              // Allow address lines to wrap
                              wordBreak: "break-word",
                            }}
                          >
                            {a.address1}
                            {a.address2 ? `, ${a.address2}` : ""},{" "}
                            {a.city}, {a.state} {a.pincode}
                          </Typography>

                          {/* Edit / Delete */}
                          <Stack direction="row" spacing={0.8} mt={1}>
                            <Button
                              size="small"
                              startIcon={<EditIcon sx={{ fontSize: "12px !important" }} />}
                              onClick={() => onEditAddress(a)}
                              sx={{
                                textTransform: "none",
                                fontSize: { xs: 10, sm: 11 },
                                fontWeight: 600,
                                borderRadius: 0.2,
                                color: "#2563eb",
                                border: "1px solid #dbeafe",
                                bgcolor: "#eff6ff",
                                minHeight: 30,
                                "&:hover": { bgcolor: "#dbeafe" },
                              }}
                            >
                              Edit
                            </Button>

                            <Button
                              size="small"
                              startIcon={<DeleteOutlineIcon sx={{ fontSize: "12px !important" }} />}
                              onClick={() => onDeleteAddress(a.id)}
                              sx={{
                                textTransform: "none",
                                fontSize: { xs: 10, sm: 11 },
                                fontWeight: 600,
                                borderRadius: 0.2,
                                color: "#dc2626",
                                border: "1px solid #fecaca",
                                bgcolor: "#fef2f2",
                                minHeight: 30,
                                "&:hover": { bgcolor: "#fecaca" },
                              }}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}

          </CardContent>
        </Card>

        {/* ══════════════════════════════
            SUPPORT
        ══════════════════════════════ */}
        <Card
          sx={{
            borderRadius: 0.2,
            border: "1px solid #e8f0fe",
            boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
            bgcolor: "#fff",
          }}
        >
          <CardContent sx={{ px: { xs: 1.5, sm: 2 }, pt: { xs: 1.5, sm: 2 }, pb: "12px !important" }}>

            <SectionHeader icon={<SupportAgentIcon />} title="Support" />

            <Divider sx={{ borderColor: "#f1f5f9", mt: 1.4 }} />

            <LinkRow
              icon={<PhoneIcon />}
              label="93218 68767"
              href="tel:+919321868767"
            />
            <LinkRow
              icon={<EmailIcon />}
              label="hello@goelectronix.com"
              href="mailto:hello@goelectronix.com"
              isLast
            />

          </CardContent>
        </Card>

        {/* ══════════════════════════════
            LEGAL
        ══════════════════════════════ */}
        <Card
          sx={{
            borderRadius: 0.2,
            border: "1px solid #e8f0fe",
            boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
            bgcolor: "#fff",
          }}
        >
          <CardContent sx={{ px: { xs: 1.5, sm: 2 }, pt: { xs: 1.5, sm: 2 }, pb: "12px !important" }}>

            <SectionHeader icon={<GavelIcon />} title="Legal" />

            <Divider sx={{ borderColor: "#f1f5f9", mt: 1.4 }} />

            <LinkRow
              icon={<PrivacyTipIcon />}
              label="Privacy Policy"
              href="https://www.goelectronix.com/privacy.php"
              isExternal
            />
            <LinkRow
              icon={<ArticleIcon />}
              label="Terms & Conditions"
              href="https://www.goelectronix.com/termandcondition.php"
              isExternal
            />
            <LinkRow
              icon={<AssignmentReturnIcon />}
              label="Return & Refund Policy"
              href="https://www.goelectronix.com/return-refund-policy.php"
              isExternal
              isLast
            />

          </CardContent>
        </Card>

        {/* ══════════════════════════════
            LOGOUT
        ══════════════════════════════ */}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<LogoutIcon />}
          onClick={logout}
          sx={{
            borderRadius: 0.2,
            py: { xs: 1.2, sm: 1.3 },
            minHeight: 48,
            textTransform: "none",
            fontWeight: 700,
            fontSize: { xs: 13, sm: 14 },
            color: "#dc2626",
            borderColor: "#fecaca",
            bgcolor: "#fef2f2",
            "&:hover": {
              borderColor: "#fca5a5",
              bgcolor: "#fee2e2",
            },
            "&:active": {
              bgcolor: "#fecaca",
            },
            transition: "all 0.18s ease",
          }}
        >
          Log Out
        </Button>

      </Stack>
    </>
  );
}